import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import type { PaginatedResponse } from "@/shared/http";
import type { SearchQueryBody } from "@/types";

import { ActionRecord } from "../ActionRecord";
import { type ActionQueryScope } from "../ActionService";

/**
 * Fetch one page of the actions in `scope` as `query` describes it: its
 * condition, sort, page size and token all go to the server, which answers
 * with that page and a token for the next one when there is one.
 */
export function useActionsPage(
  scope: ActionQueryScope,
  query: SearchQueryBody,
) {
  const { actionService } = useDomainServices();
  const { currentOrganization } = useAuth();

  return useQuery<PaginatedResponse<ActionRecord>, Error>({
    queryKey: ["actions", "page", scope, currentOrganization?.org_id, query],
    queryFn({ signal }) {
      if (currentOrganization === null) {
        return Promise.reject(new Error("Requires login"));
      }

      return actionService.queryActions(scope, {
        abortSignal: signal,
        orgId: currentOrganization.org_id,
        condition: query.condition,
        pageToken: query.after ?? undefined,
        limit: query.limit,
        sortBy: query.sort_by ?? undefined,
        sortDirection: query.sort_direction,
      });
    },
    enabled: currentOrganization !== null,
    // A changed filter, sort or page keeps the last rows on screen until the
    // new ones land.
    placeholderData: keepPreviousData,
  });
}
